import {
  Actor,
  ActorTransform,
  Character,
  Characters,
  Globals,
  MathOperation,
  Position,
  RuleExtent,
  RuleTreeItem,
  RuleValue,
  Stage,
  StageVariable,
  VariableComparator,
} from "../../types";
import { RELATIVE_TRANSFORMS } from "../components/inspector/transform-lookup";
import { DEFAULT_APPEARANCE_INFO } from "../components/sprites/sprite";
import { STAGE_CELL_SIZE } from "../constants/constants";
import { getStageBackground, getStageHeight, getStageWidth } from "./builtin-stage-variables";

export function buildActorSelection(worldId: string, stageId: string, actorIds: string[]) {
  return { worldId, stageId, actorIds };
}

export function applyAnchorAdjustment(
  position: Position,
  character: Character,
  { appearance, transform }: Pick<Actor, "appearance" | "transform">,
) {
  const info = character.spritesheet.appearanceInfo?.[appearance] || DEFAULT_APPEARANCE_INFO;
  const [x, y] = pointApplyingTransform(info.anchor.x, info.anchor.y, info, transform);
  // Image-space Y is Y-down (0 = top of image) but world-space Y is Y-up
  // (1 = bottom of stage), so the image-anchor Y contribution flips sign.
  position.x += x;
  position.y -= y;
}

/**
 * The shift to apply to an actor's `position` so its artwork stays in exactly
 * the same squares when its appearance's anchor square moves from `prevAnchor`
 * to `nextAnchor`.
 *
 * The anchor's contribution to where a sprite renders is `applyAnchorAdjustment`
 * (the transformed anchor, with the y component flipped because world Y is up
 * while the image is Y-down), so the position must move by the change in that
 * contribution. We get it by transforming both anchors and subtracting; the
 * width/height terms in `pointApplyingTransform` are constants that cancel in
 * the subtraction, so the sprite's dimensions are not needed here.
 */
export function positionDeltaForAnchorChange(
  prevAnchor: Position,
  nextAnchor: Position,
  transform: Actor["transform"],
): Position {
  const dims = { width: 0, height: 0 }; // cancels in the subtraction below
  const [px, py] = pointApplyingTransform(prevAnchor.x, prevAnchor.y, dims, transform);
  const [nx, ny] = pointApplyingTransform(nextAnchor.x, nextAnchor.y, dims, transform);
  return { x: nx - px, y: -(ny - py) };
}

/**
 * Convert a pixel offset within the stage element into a stage square.
 *
 * `px` and `dragOffset` are both in *screen* pixels: the stage is rendered with
 * a CSS zoom of `scale`, so one square is `STAGE_CELL_SIZE * scale` pixels
 * on screen. `dragOffset` is where inside the dragged sprite the cursor grabbed
 * it; a plain click has no such offset, so we use half a square, which turns
 * the `Math.round` below into a `Math.floor`.
 *
 * Both offsets have to be measured in the same (screen) units — using an
 * unscaled half-square here left clicks near the bottom/right of a square
 * resolving to the neighbouring square on any stage whose zoom wasn't 1.
 */
export function stageSquareForPixelOffset(
  px: { left: number; top: number },
  {
    scale,
    stageHeight,
    dragOffset,
  }: { scale: number; stageHeight: number; dragOffset?: { dragLeft: number; dragTop: number } },
): Position {
  const squarePx = STAGE_CELL_SIZE * scale;
  const { dragLeft, dragTop } = dragOffset ?? { dragLeft: squarePx / 2, dragTop: squarePx / 2 };

  const rowFromTop = Math.round((px.top - dragTop) / squarePx);
  const colFromLeft = Math.round((px.left - dragLeft) / squarePx);

  // Y-up, 1-indexed: top row = stageHeight, bottom row = 1.
  return { x: colFromLeft + 1, y: stageHeight - rowFromTop };
}

export function stagePositionsBetween(from: Position, to: Position): Position[] {
  const positions: Position[] = [];
  let x = from.x;
  let y = from.y;
  const dx = Math.abs(to.x - from.x);
  const dy = Math.abs(to.y - from.y);
  const stepX = from.x < to.x ? 1 : -1;
  const stepY = from.y < to.y ? 1 : -1;
  let error = dx - dy;

  while (true) {
    positions.push({ x, y });
    if (x === to.x && y === to.y) {
      return positions;
    }
    const doubledError = error * 2;
    if (doubledError > -dy) {
      error -= dy;
      x += stepX;
    }
    if (doubledError < dx) {
      error += dx;
      y += stepY;
    }
  }
}

export function actorFillsPoint(actor: Actor, characters: Characters, point: Position): boolean {
  return actorFilledPoints(actor, characters).some((p) => p.x === point.x && p.y === point.y);
}

export function sortActorsByZOrder(actors: Actor[], characterZOrder: string[]): Actor[] {
  return [...actors].sort((a, b) => {
    const zA = characterZOrder.indexOf(a.characterId);
    const zB = characterZOrder.indexOf(b.characterId);
    return (zA === -1 ? -1 : zA) - (zB === -1 ? -1 : zB);
  });
}

/**
 * Returns the ids of `actors` in the order a tick should visit them: the
 * character drawn on top of the others goes first, so execution order follows
 * the layering the user already sees on the stage rather than the order actors
 * happen to sit in the stage's dictionary. A higher index in `characterZOrder`
 * means a higher z-index (drawn on top), so this is the reverse of
 * `sortActorsByZOrder`. Characters missing from `characterZOrder` are treated
 * as bottom-most, matching that function.
 *
 * Actors of the same character are ordered by id: deterministic, but not an
 * order the user is expected to reason about.
 */
export function sortActorIdsByTickOrder(
  actors: { [actorId: string]: Actor },
  characterZOrder: string[],
): string[] {
  return Object.values(actors)
    .sort(
      (a, b) =>
        characterZOrder.indexOf(b.characterId) - characterZOrder.indexOf(a.characterId) ||
        a.id.localeCompare(b.id),
    )
    .map((actor) => actor.id);
}

export function actorsAtPoint(
  actors: { [id: string]: Actor },
  characters: Characters,
  point: Position,
  characterZOrder?: string[],
): Actor[] {
  const result = Object.values(actors).filter((actor) => actorFillsPoint(actor, characters, point));
  if (characterZOrder) {
    return sortActorsByZOrder(result, characterZOrder);
  }
  return result;
}

export function actorIntersectsExtent(actor: Actor, characters: Characters, extent: RuleExtent) {
  const points = new Set(actorFilledPoints(actor, characters).map((p) => `${p.x},${p.y}`));
  for (let x = extent.xmin; x <= extent.xmax; x++) {
    for (let y = extent.ymin; y <= extent.ymax; y++) {
      if (points.has(`${x},${y}`)) return true;
    }
  }
  return false;
}

export function actorFilledPoints(actor: Actor, characters: Characters) {
  const character = characters[actor.characterId];
  if (!character) {
    console.warn(
      `actorFilledPoints: character ${actor.characterId} not found for actor ${actor.id}`,
    );
    return [actor.position];
  }
  const info = character.spritesheet.appearanceInfo?.[actor.appearance];
  const { x, y } = actor.position;
  if (!info) {
    // No appearance info is normal for simple 1x1 sprites
    return [{ x, y }];
  }
  const results: Position[] = [];
  const [ix, iy] = pointApplyingTransform(info.anchor.x, info.anchor.y, info, actor.transform);

  for (let dx = 0; dx < info.width; dx++) {
    for (let dy = 0; dy < info.height; dy++) {
      if (info.filled[`${dx},${dy}`]) {
        const [sx, sy] = pointApplyingTransform(dx, dy, info, actor.transform);
        // Image-space Y is Y-down (0 = top of image) but world Y is Y-up
        // (1 = bottom of stage), so flip the sign of the y delta.
        results.push({ x: x + sx - ix, y: y + iy - sy });
      }
    }
  }
  return results;
}

export function pointIsOutside({ x, y }: Position, { xmin, xmax, ymin, ymax }: RuleExtent) {
  return x < xmin || x > xmax || y < ymin || y > ymax;
}

export function pointIsInside(a: Position, b: RuleExtent) {
  return !pointIsOutside(a, b);
}

export function pointByAdding({ x, y }: Position, { x: dx, y: dy }: Position) {
  return { x: x + dx, y: y + dy };
}

export function pointApplyingTransform(
  x: number,
  y: number,
  { width, height }: { width: number; height: number },
  transform: Actor["transform"],
) {
  if (transform === "90") {
    return [height - 1 - y, x];
  }
  if (transform === "270") {
    return [y, width - 1 - x];
  }
  if (transform === "180") {
    return [width - 1 - x, height - 1 - y];
  }
  if (transform === "flip-x") {
    return [width - 1 - x, y];
  }
  if (transform === "flip-y") {
    return [x, height - 1 - y];
  }
  if (transform === "d1") {
    return [y, x];
  }
  if (transform === "d2") {
    return [height - 1 - y, width - 1 - x];
  }
  return [x, y];
}

/**
 * Calculate the visual bounds and position offset needed to render a transformed
 * sprite within a container. When a transform (rotation/flip) is applied, the
 * visual bounding box changes and may shift relative to the anchor point.
 *
 * Returns the container dimensions (in cells) and the position offset needed
 * so the sprite's visual fits starting at (0, 0).
 */
export function getTransformedBounds(
  info: { width: number; height: number; anchor: { x: number; y: number } },
  transform: Actor["transform"],
): { offsetX: number; offsetY: number; width: number; height: number } {
  // Transform anchor and corners to find the bounding box
  const [anchorX, anchorY] = pointApplyingTransform(info.anchor.x, info.anchor.y, info, transform);

  // Transform all four corners and find min/max
  const corners = [
    [0, 0],
    [info.width - 1, 0],
    [0, info.height - 1],
    [info.width - 1, info.height - 1],
  ].map(([x, y]) => {
    const [tx, ty] = pointApplyingTransform(x, y, info, transform);
    return [tx - anchorX, ty - anchorY];
  });

  const minX = Math.min(...corners.map(([x]) => x));
  const maxX = Math.max(...corners.map(([x]) => x));
  const minY = Math.min(...corners.map(([, y]) => y));
  const maxY = Math.max(...corners.map(([, y]) => y));

  return {
    offsetX: -minX,
    offsetY: -minY,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  };
}

export function shuffleArray<T>(input: Array<T>): Array<T> {
  const d = [...input]; // Create a copy to avoid mutating input
  for (let c = d.length - 1; c > 0; c--) {
    const b = Math.floor(Math.random() * (c + 1));
    const a = d[c];
    d[c] = d[b];
    d[b] = a;
  }
  return d;
}

/**
 * Bundled context for resolving a RuleValue. Grouping these together avoids
 * threading 4+ positional arguments through every call site, and means adding
 * a new value tier later (akin to stage variables) is a one-field change here.
 */
export type RuleValueContext = {
  globals: Globals;
  characters: Characters;
  /** The stage whose variableValues a `{stageVariableId}` should resolve against. */
  stage?: Pick<Stage, "variableValues">;
  stageVariables?: Record<string, StageVariable>;
};

export function resolveRuleValue(
  val: RuleValue,
  comparator: VariableComparator, // some values behave differently depending on the comparator
  stageActorForId: Stage["actors"],
  ctx: RuleValueContext,
): string | null {
  if (!val) {
    console.warn(`A rule value is missing?`);
    return "";
  }
  if ("constant" in val) {
    return val.constant;
  }
  if ("actorId" in val) {
    const actor = stageActorForId[val.actorId];
    if (!actor) {
      console.warn(`resolveRuleValue: actor ${val.actorId} not found`);
      return null;
    }
    const character = ctx.characters[actor.characterId];
    if (!character) {
      console.warn(`resolveRuleValue: character ${actor.characterId} not found`);
      return null;
    }
    return getVariableValue(actor, character, val.variableId, comparator);
  }
  if ("globalId" in val) {
    const value = ctx.globals[val.globalId]?.value;

    // If the rule says "global X must be actor Y", "Y" is an ID within the rule.
    // We need to map it to that actor on the stage for it to ever evaluate to true.
    if (value && value.startsWith("actor:")) {
      return Object.keys(stageActorForId).find((id) => stageActorForId[id].id === value) ?? value;
    }
    return value;
  }
  if ("stageVariableId" in val) {
    // Every defined stage variable is required to have a value on every stage,
    // so we read directly without falling back to a world-level default. A
    // missing value here means either the variable was deleted (the rule
    // should have been scrubbed) or the seeding invariant was violated.
    return ctx.stage?.variableValues?.[val.stageVariableId] ?? null;
  }
  if ("random" in val) {
    return String(rollRandomInRange(val.random.min, val.random.max));
  }
  isNever(val);
  return "";
}

/** The default range of the dice condition: "random < 25" matches 25% of the time. */
export const RANDOM_CONDITION_RANGE = { min: 0, max: 99 };

/** A uniformly distributed integer between min and max, inclusive. */
export function rollRandomInRange(min: number, max: number) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

/**
 * Parse a "variable" drag-and-drop payload (set by VariableGridItem) into the
 * RuleValue it represents. Centralizes the three-way discrimination so condition
 * drop sites don't each reimplement the same if/else ladder.
 */
export function ruleValueFromDragPayload(
  raw: string,
): Exclude<RuleValue, { constant: string } | { random: unknown }> | null {
  const p = JSON.parse(raw) as {
    actorId?: string;
    globalId?: string;
    stageVariableId?: string;
    variableId?: string;
  };
  if (p.stageVariableId) return { stageVariableId: p.stageVariableId };
  if (p.globalId) return { globalId: p.globalId };
  if (p.actorId && p.variableId) return { actorId: p.actorId, variableId: p.variableId };
  return null;
}

/** Why does the value of a variable depend on `comparator`? It's gross, but we
 * want to allow an appearances to be compared-by-identity against an appearance ID,
 * or compared-by-name against a string. They can be renamed, and renaming one shouldn't
 * break rules saying "appearance = foo".
 *
 * Alternatively we could make "= foo" a dynamic lookup of the appearance name, but the
 * game doesn't currently enforce that appearances need to have unique names.
 */
export function getVariableValue(
  actor: Actor,
  character: Character,
  id: string,
  comparator: VariableComparator,
) {
  if (id === "appearance") {
    if (["=", "!="].includes(comparator)) {
      return actor.appearance ?? null;
    }
    return character.spritesheet.appearanceNames[actor.appearance];
  }
  if (id === "transform") {
    return actor.transform ?? "0";
  }
  if (id === "x") {
    return String(actor.position.x);
  }
  if (id === "y") {
    return String(actor.position.y);
  }
  if (actor.variableValues[id] !== undefined) {
    return actor.variableValues[id] ?? null;
  }
  if (character.variables[id] !== undefined) {
    return character.variables[id].defaultValue ?? null;
  }
  return null;
}

// Inverse transforms in D4 symmetry group
export const INVERSE_TRANSFORMS: { [key in ActorTransform]: ActorTransform } = {
  "0": "0",
  "90": "270",
  "180": "180",
  "270": "90",
  "flip-x": "flip-x",
  "flip-y": "flip-y",
  d1: "d1",
  d2: "d2",
};

/**
 * Whether `value` names one of the eight orientations in the D4 group. The
 * value handed to a `transform` action comes from a rule and can be any
 * string — a color, a score, a half-typed word — so it's checked rather than
 * trusted.
 */
export function isActorTransform(value: string | null | undefined): value is ActorTransform {
  return value !== null && value !== undefined && value in INVERSE_TRANSFORMS;
}

export function applyTransformOperation(
  existing: ActorTransform,
  operation: MathOperation,
  value: ActorTransform,
) {
  // Either side can arrive as something that isn't an orientation. Falling
  // back to "0" for the actor's current transform (its default) and leaving
  // the actor alone for an unusable operand keeps a bad rule from silently
  // un-rotating a sprite, which reads as a bug in the sprite rather than in
  // the value that caused it.
  const from = isActorTransform(existing) ? existing : "0";
  if (!isActorTransform(value)) {
    return from;
  }
  if (operation === "add") {
    return RELATIVE_TRANSFORMS[from][value];
  }
  if (operation === "subtract") {
    // Subtract is composition with the inverse: existing * inverse(value)
    return RELATIVE_TRANSFORMS[from][INVERSE_TRANSFORMS[value]];
  }
  if (operation === "set") {
    return value;
  }

  throw new Error(`applyTransformOperation unknown operation ${operation}`);
}

export function applyVariableOperation(existing: string, operation: MathOperation, value: string) {
  if (operation === "add") {
    return `${Number(existing) + Number(value)}`;
  }
  if (operation === "subtract") {
    return `${Number(existing) - Number(value)}`;
  }
  if (operation === "set") {
    return `${value}`;
  }

  throw new Error(`applyVariableOperation unknown operation ${operation}`);
}

export type RulePredicate = (rule: RuleTreeItem) => boolean;

export type FindRulesResult = Array<{
  rule: RuleTreeItem;
  parent: { rules: RuleTreeItem[] };
  index: number;
}>;

/**
 * Find all rules matching a predicate function.
 * Returns an array of objects with the rule, its parent container, and index.
 */
export function findRules(
  node: { rules: RuleTreeItem[] },
  predicate: RulePredicate,
): FindRulesResult {
  const results: FindRulesResult = [];

  if (!("rules" in node)) {
    return results;
  }

  for (let idx = 0; idx < node.rules.length; idx++) {
    const rule = node.rules[idx];

    if (predicate(rule)) {
      results.push({ rule, parent: node, index: idx });
    }

    if ("rules" in rule && rule.rules) {
      results.push(...findRules(rule, predicate));
    }
  }

  return results;
}

/**
 * Find a single rule by ID.
 * Returns [rule, parent, index] or [null, {rules: []}, 0] if not found.
 */
export function findRule(
  node: { rules: RuleTreeItem[] },
  id: string,
): [RuleTreeItem | null, { rules: RuleTreeItem[] }, number] {
  const results = findRules(node, (rule) => rule.id === id);
  if (results.length > 0) {
    return [results[0].rule, results[0].parent, results[0].index];
  }
  return [null, { rules: [] }, 0] as const;
}

/**
 * Predicate to check if a rule uses a specific variable ID.
 */
export function ruleUsesVariable(variableId: string): RulePredicate {
  return (rule: RuleTreeItem) => {
    // Check if this is a flow item with a loop count referencing the variable
    if (
      rule.type === "group-flow" &&
      "behavior" in rule &&
      rule.behavior === "loop" &&
      "loopCount" in rule &&
      "variableId" in rule.loopCount &&
      rule.loopCount.variableId === variableId
    ) {
      return true;
    }

    // Check if this is a rule with conditions or actions using the variable
    if (rule.type === "rule") {
      // Check conditions
      const hasConditionUsingVar = rule.conditions.some(
        (c) =>
          ("actorId" in c.left && "variableId" in c.left && c.left.variableId === variableId) ||
          ("actorId" in c.right && "variableId" in c.right && c.right.variableId === variableId),
      );

      // Check actions
      const hasActionUsingVar = rule.actions.some(
        (a) =>
          (a.type === "variable" && a.variable === variableId) ||
          ("value" in a &&
            a.value &&
            "actorId" in a.value &&
            "variableId" in a.value &&
            a.value.variableId === variableId),
      );

      if (hasConditionUsingVar || hasActionUsingVar) {
        return true;
      }
    }

    // Check flow item check conditions
    if (rule.type === "group-flow" && rule.check) {
      const hasCheckConditionUsingVar = rule.check.conditions.some(
        (c) =>
          ("actorId" in c.left && "variableId" in c.left && c.left.variableId === variableId) ||
          ("actorId" in c.right && "variableId" in c.right && c.right.variableId === variableId),
      );
      if (hasCheckConditionUsingVar) {
        return true;
      }
    }

    return false;
  };
}
type HTMLImageElementLoaded = HTMLImageElement & { _codakoloaded?: boolean };

let bgImages: { [url: string]: HTMLImageElementLoaded } = {};

function cssURLToURL(cssUrl: string) {
  if (cssUrl.includes("/Layer0_2.png")) {
    return new URL(`/src/editor/img/backgrounds/Layer0_2.png`, import.meta.url).href;
  }
  if (cssUrl.includes("url(")) {
    return cssUrl.split("url(").pop()!.slice(0, -1).replace(/['"]$/, "").replace(/^['"]/, "");
  }
  return null;
}

export function prepareCrossoriginImages(stages: Stage[]) {
  const next: { [url: string]: HTMLImageElementLoaded } = {};

  for (const stage of stages) {
    const url = cssURLToURL(getStageBackground(stage));
    if (!url) continue;

    next[url] = bgImages[url];
    if (!next[url]) {
      const background = new Image() as HTMLImageElementLoaded;
      background.crossOrigin = "anonymous";
      background._codakoloaded = false;
      background.onload = () => {
        background._codakoloaded = true;
      };
      background.onerror = (e) => {
        console.error(`Failed to load image: ${background.src}: ${e}`);
      };
      background.src = url;
      next[url] = background;
    }
  }
  bgImages = next;
}

export function applyActorTransformToContext(
  context: CanvasRenderingContext2D,
  transform: ActorTransform,
) {
  switch (transform || "0") {
    case "90":
      context.rotate((90 * Math.PI) / 180);
      break;
    case "180":
      context.rotate((180 * Math.PI) / 180);
      break;
    case "270":
      context.rotate((270 * Math.PI) / 180);
      break;
    case "flip-x":
      context.scale(-1, 1);
      break;
    case "flip-y":
      context.scale(1, -1);
      break;
    case "d1":
      context.rotate((90 * Math.PI) / 180);
      context.scale(1, -1);
      break;
    case "d2":
      context.rotate((-90 * Math.PI) / 180);
      context.scale(1, -1);
      break;
    case "0":
      break;
    default:
      throw new Error(`Unsupported transform ${transform}`);
  }
}

/**
 * Returns whether the given transform swaps width and height dimensions.
 * This is true for 90°, 270°, and diagonal reflections.
 */
export function transformSwapsDimensions(transform: ActorTransform): boolean {
  return transform === "90" || transform === "270" || transform === "d1" || transform === "d2";
}

/**
 * Renders an image with the given transform applied to a new canvas.
 * Returns the canvas element with the transformed image drawn on it.
 */
export function renderTransformedImage(
  img: CanvasImageSource,
  imgWidth: number,
  imgHeight: number,
  transform: ActorTransform,
): HTMLCanvasElement {
  const needsSwap = transformSwapsDimensions(transform);
  const canvasWidth = needsSwap ? imgHeight : imgWidth;
  const canvasHeight = needsSwap ? imgWidth : imgHeight;

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext("2d");

  if (ctx) {
    ctx.save();
    ctx.translate(canvasWidth / 2, canvasHeight / 2);
    applyActorTransformToContext(ctx, transform);
    ctx.drawImage(img, -imgWidth / 2, -imgHeight / 2);
    ctx.restore();
  }

  return canvas;
}

export function getStageScreenshot(stage: Stage, { size }: { size: number }) {
  const { characters, characterZOrder } = window.editorStore!.getState();

  const width = getStageWidth(stage);
  const height = getStageHeight(stage);
  const scale = Math.min(size / (width * 40), size / (height * 40));
  const pxPerSquare = Math.round(40 * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width * pxPerSquare;
  canvas.height = height * pxPerSquare;
  const context = canvas.getContext("2d");
  if (!context) {
    return;
  }
  const background = getStageBackground(stage);
  const backgroundUrl = cssURLToURL(background);
  if (backgroundUrl) {
    const backgroundImage = bgImages[backgroundUrl];
    if (backgroundImage && backgroundImage._codakoloaded) {
      context.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);
    }
  } else {
    context.fillStyle = background;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  sortActorsByZOrder(Object.values(stage.actors), characterZOrder).forEach((actor) => {
    const character = characters[actor.characterId];
    if (!character) return;
    const { appearances, appearanceInfo } = character.spritesheet;
    const data = appearances[actor.appearance]?.[0];
    if (!data) return;
    const i = new Image();
    i.src = data;
    const info = appearanceInfo?.[actor.appearance] || DEFAULT_APPEARANCE_INFO;

    context.save();
    // Y-up, 1-indexed world: cell (x, y) has its center on the canvas at
    // ((x - 0.5) * px, (height - y + 0.5) * px) from the top-left.
    context.translate(
      Math.floor((actor.position.x - 0.5) * pxPerSquare),
      Math.floor((height - actor.position.y + 0.5) * pxPerSquare),
    );
    applyActorTransformToContext(context, actor.transform ?? "0");
    context.drawImage(
      i,
      -(info.anchor.x + 0.5) * pxPerSquare,
      -(info.anchor.y + 0.5) * pxPerSquare,
      info.width * pxPerSquare,
      info.height * pxPerSquare,
    );
    context.restore();
  });

  try {
    return canvas.toDataURL("image/jpeg", 0.8);
  } catch (err) {
    console.warn(`getStageScreenshot: ${err}`);
  }
  return null;
}

export function isNever(val: never): never {
  throw new Error(`Expected var to be never but it is ${JSON.stringify(val)}.`);
}

export function comparatorMatches(
  comparator: VariableComparator,
  a: string | null,
  b: string | null,
): boolean {
  switch (comparator) {
    case "=":
      return `${a}` === `${b}`;
    case "!=":
      return `${a}` != `${b}`;
    case ">=":
      return Number(a) >= Number(b);
    case "<=":
      return Number(a) <= Number(b);
    case ">":
      return Number(a) > Number(b);
    case "<":
      return Number(a) < Number(b);
    case "contains":
      if (`${a}`.includes(",")) {
        // This is a special hack for keypress so "ArrowLeft,Space" doesn't match "A"
        return a?.split(",").some((v) => v === b) ?? false;
      }
      return `${a}`.includes(`${b}`);
    case "ends-with":
      return `${a}`.endsWith(`${b}`);
    case "starts-with":
      return `${a}`.startsWith(`${b}`);
    default:
      isNever(comparator);
  }
}
